import { FC, useRef, useState } from "react";

import { Stack } from "@mui/material";
import {
  browserDefaultTimezone,
  formatDateToMediumDateTimeWithTimezone,
  generateOccurrences,
  getDatePlusNHours,
  isDrawingFormCategory,
  isRecurrenceDateBoundErrorCode,
  MAX_OCCURRENCE_LIMIT,
  TicketingFormCategory,
  TicketingRecurrenceType,
} from "@simplyk/common";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";

import { trpc } from "../../../../../common/helpers/trpc";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { getPlaceholderOccurrenceWithRates, getPlaceholderRates } from "../../common/prefillDataBuilders/prefillRates";
import { mapRecurrencesToOccurrencesWithRates } from "../../common/useUpdateLiveTicketingPreview/useBuildInitialLiveTicketingData";
import { useUpdateTicketingDataInPreview } from "../../common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { FormStepContainer } from "../FormStepContainer";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { TransferPurchasesToDateDialog } from "./TransferPurchasesToDateDialog";
import { TicketingRecurrenceData } from "./types";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { TicketingRecurrenceList } from "@/features/LiveFormEditor/Steps/StepDate/TicketingRecurrenceList";
import { Translations } from "@/public/@types/translations";

export type StepDateFormData = {
  recurrences?: TicketingRecurrenceData[];
};

const getDateStepTranslationKeys = (
  category: TicketingFormCategory
): {
  title: keyof Translations["dashboard"];
  description?: keyof Translations["dashboard"];
} => {
  const translationKeys = {
    [TicketingFormCategory.Lottery]: {
      title: "ticketing.dateOfLottery.title",
      description: "ticketing.dateOfLottery.description",
    },
    [TicketingFormCategory.Sweepstakes]: {
      title: "sweepstakes.date.title",
      description: "sweepstakes.date.description",
    },
    [TicketingFormCategory.Auction]: {
      title: "ticketing.dateOfAuction.title",
    },
    [TicketingFormCategory.Custom]: {
      title: "ticketing.dateOfCustom.title",
      description: "ticketing.dateOfCustom.description",
    },
    default: {
      title: "ticketing.dateOfEvent.title",
      description: "ticketing.dateOfEvent.description",
    },
  } as const;
  switch (category) {
    case TicketingFormCategory.Lottery:
    case TicketingFormCategory.Sweepstakes:
    case TicketingFormCategory.Auction:
    case TicketingFormCategory.Custom:
      return translationKeys[category];
    default:
      return translationKeys.default;
  }
};

export const getNewRecurrence = (): TicketingRecurrenceData => ({
  id: v4(),
  type: TicketingRecurrenceType.Once,
  startUtc: null,
  endUtc: null,
  closeDate: null,
  openDate: null,
  repeatUntilDate: null,
  isHidden: false,
});

const getRecurrenceEndUtc = ({ startUtc, endUtc }: { startUtc: Date | string; endUtc: Date | string }): Date => {
  const start = new Date(startUtc);
  const end = new Date(endUtc);
  return end > start ? end : getDatePlusNHours(start, 1);
};

const getClosestOccurrenceStartDate = (
  recurrences: TicketingRecurrenceData[] | undefined,
  timeZone: string
): Date | null => {
  const now = Date.now();
  return (recurrences ?? [])
    .filter((recurrence): recurrence is TicketingRecurrenceData & { startUtc: Date; endUtc: Date } =>
      Boolean(recurrence.startUtc && recurrence.endUtc)
    )
    .flatMap((recurrence) =>
      generateOccurrences({
        recurrence: {
          type: recurrence.type || TicketingRecurrenceType.Once,
          startUtc: recurrence.startUtc,
          endUtc: recurrence.endUtc,
          repeatUntilDate: recurrence.repeatUntilDate,
        },
        limit: MAX_OCCURRENCE_LIMIT,
        timeZone,
      })
    )
    .reduce<Date | null>(
      (closest, occurrence) =>
        !closest || Math.abs(occurrence.startUtc.getTime() - now) < Math.abs(closest.getTime() - now)
          ? occurrence.startUtc
          : closest,
      null
    );
};

export const TicketingLiveEditorStepDate: FC = () => {
  const { t } = useTranslate();
  const { apiFormObject: apiTicketing } = useLiveFormEditorContext<"ticketing">();
  const { formCategory } = apiTicketing;
  const { liveFormAmplitudePayload } = useLiveFormAmplitudeEvents();

  const defaultValues: StepDateFormData = {
    recurrences:
      (formCategory === TicketingFormCategory.Auction || isDrawingFormCategory(formCategory)) &&
      !apiTicketing.recurrences?.length
        ? [getNewRecurrence()]
        : apiTicketing.recurrences?.map((recurrence) => ({
            ...recurrence,
            startUtc: new Date(recurrence.startUtc),
            endUtc: getRecurrenceEndUtc(recurrence),
            closeDate: recurrence.closeDate ?? null,
            openDate: recurrence.openDate ?? null,
            repeatUntilDate: recurrence.repeatUntilDate ? new Date(recurrence.repeatUntilDate) : null,
            isHidden: recurrence.isHidden ?? false,
          })) || [],
  };
  const form = useForm<StepDateFormData>({ defaultValues });

  const updateTicketingDate = trpc.updateTicketingDate.useMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepDateFormData>({
    type: "ticketing",
    form,
    saveData: async (formData) => {
      const eventTimezone = apiTicketing.eventTimezone || browserDefaultTimezone;
      const result = await updateTicketingDate.mutateAsync({
        ticketingId: apiTicketing.id,
        eventTimezone,
        recurrences:
          formData.recurrences?.map((recurrence) => ({
            id: recurrence.id,
            type: recurrence.type || TicketingRecurrenceType.Once,
            startUtc: recurrence.startUtc || new Date(),
            endUtc: recurrence.endUtc || new Date(),
            repeatUntilDate: recurrence.type === TicketingRecurrenceType.Once ? null : recurrence.repeatUntilDate,
            closeDate: recurrence.closeDate ? new Date(recurrence.closeDate) : null,
            openDate: recurrence.openDate ? new Date(recurrence.openDate) : null,
            isHidden: recurrence.isHidden ?? false,
          })) || [],
      });
      if (result.error && isRecurrenceDateBoundErrorCode(result.error.code)) {
        enqueueSnackbar(t("dashboard", "ticketing.dateBefore2018Error"), { vibe: "danger" });
        return { ...result, error: { ...result.error, silent: true } };
      }
      return result;
    },
  });

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      const occurrencesWithRates = formData.recurrences?.length
        ? mapRecurrencesToOccurrencesWithRates(
            formData.recurrences,
            getPlaceholderRates({
              formCategory,
              t,
              locale: apiTicketing.locale,
            }),
            apiTicketing.id
          )
        : // If there are no occurrences, we display a placeholder occurrence to show the users what they can add
          [
            getPlaceholderOccurrenceWithRates({
              formCategory,
              t,
              locale: apiTicketing.locale,
            }),
          ];

      liveDataToMutate.occurrencesWithRates = occurrencesWithRates;
      liveDataToMutate.selectedOccurrenceWithRates = occurrencesWithRates[0] || null;
      liveDataToMutate.placeholders.isDate = !formData.recurrences?.length;
    },
  });

  const timezone = apiTicketing.eventTimezone || browserDefaultTimezone;
  const recurrences = form.watch("recurrences");

  const { data: recurrencesHasSales, isLoading: isRecurrencesHasSalesLoading } = trpc.getRecurrencesHasSales.useQuery(
    { ticketingId: apiTicketing.id },
    { enabled: Boolean(apiTicketing.id) }
  );

  const shouldShowSkipStep = !isDrawingFormCategory(formCategory) && !recurrences?.length && !form.formState.isDirty;

  const hadNoDates = !apiTicketing.recurrences?.length;
  const { locale } = useLocaleContext();
  const utils = trpc.useUtils();
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const pendingNavigationRef = useRef<"next" | "back">("next");

  const closestStartDate = getClosestOccurrenceStartDate(recurrences, timezone);

  const shouldConfirmTransfer = async (): Promise<boolean> => {
    if (!hadNoDates || !closestStartDate || !form.formState.isDirty) {
      return false;
    }
    return await utils.hasTicketingSalesWithoutOccurrence.fetch({ ticketingId: apiTicketing.id });
  };

  const handleNext = async () => {
    if (await shouldConfirmTransfer()) {
      pendingNavigationRef.current = "next";
      setIsTransferDialogOpen(true);
      return;
    }
    await handleSaveAndNext();
  };

  const handleBack = async () => {
    if (await shouldConfirmTransfer()) {
      pendingNavigationRef.current = "back";
      setIsTransferDialogOpen(true);
      return;
    }
    await handleSaveAndBack();
  };

  const handleConfirmTransfer = async () => {
    setIsTransferDialogOpen(false);
    if (pendingNavigationRef.current === "next") {
      await handleSaveAndNext();
    } else {
      await handleSaveAndBack();
    }
  };

  const dateStepTranslations = getDateStepTranslationKeys(formCategory);

  return (
    <FormStepContainer
      title={t("dashboard", dateStepTranslations.title)}
      description={dateStepTranslations.description ? t("dashboard", dateStepTranslations.description) : ""}
      form={form}
      actionsProps={{
        onBack: handleBack,
        onNext: handleNext,
        nextButtonLabel: shouldShowSkipStep ? "skip" : "next",
      }}
    >
      <Stack rowGap={2}>
        {formCategory === TicketingFormCategory.Auction && (
          <DashboardInfobox
            vibe="brand"
            inlineLinkProps={[
              {
                key: "learnMore",
                children: t("common", "learnMore"),
                href: "https://support.zeffy.com/creating-and-configuring-an-auction-form?hs_preview=KQOzYTiE-166864159151",
                target: "_blank",
              },
            ]}
          >
            {t("dashboard", "ticketing.auctionDateAndTimeInformation")}
          </DashboardInfobox>
        )}
        {/* Date Selection Section */}
        <TicketingRecurrenceList
          form={form}
          ticketing={apiTicketing}
          timezone={timezone}
          isAuction={formCategory === TicketingFormCategory.Auction}
          amplitudePayload={liveFormAmplitudePayload}
          recurrencesHasSales={{
            data: recurrencesHasSales,
            isLoading: isRecurrencesHasSalesLoading,
          }}
        />
      </Stack>
      <TransferPurchasesToDateDialog
        open={isTransferDialogOpen}
        onClose={() => setIsTransferDialogOpen(false)}
        onConfirm={handleConfirmTransfer}
        formattedDate={
          closestStartDate ? formatDateToMediumDateTimeWithTimezone(closestStartDate, locale, timezone) : ""
        }
      />
    </FormStepContainer>
  );
};
